import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const OUTCOME_EMOJI = {
  OUTCOME_1: '✅',
  OUTCOME_2: '🔍',
  OUTCOME_3: '⚠️',
  OUTCOME_4: '🔀',
};

const SCORE_BARS = [
  { key: 'corpScore', label: 'Corporate', max: 40, color: '#1a3a5c' },
  { key: 'litScore', label: 'Litigation', max: 40, color: '#b45309' },
  { key: 'ihScore', label: 'In-house', max: 40, color: '#065f46' },
  { key: 'readinessScore', label: 'Readiness', max: 24, color: '#6d28d9' },
];

export default function AssessmentResult() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = user?.name ? user.name.split(' ')[0] : '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/assessment/result')
      .then(({ data }) => setData(data))
      .catch(() => setError('Could not load your result. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={s.page}><div style={s.spinner} /></div>;
  if (error) return <div style={s.page}><p style={s.errorTxt}>{error}</p></div>;
  if (!data) return null;

  const { result, outcomeData } = data;
  const emoji = OUTCOME_EMOJI[result.outcomeCode] || '';

  return (
    <div style={s.page}>
      <div style={s.container}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.headerNav}>
            <button style={s.backBtn} onClick={() => navigate('/assessment')}>
              &larr; Assessment
            </button>
          </div>
          <div style={s.outcomeEmoji}>{emoji}</div>
          <h1 style={s.outcomeTitle}>{firstName ? `${firstName}, ` : ''}{result.outcomeTitle}</h1>
          {result.practiceArea && (
            <div style={s.practiceArea}>Identified area: <strong>{result.practiceArea}</strong></div>
          )}
          <p style={s.outcomeDesc}>{outcomeData.description}</p>
        </div>

        {/* Score bars */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Your Scores</h3>
          <div style={s.scoreBars}>
            {SCORE_BARS.map(bar => {
              const val = result[bar.key] || 0;
              const pct = Math.round((val / bar.max) * 100);
              return (
                <div key={bar.key} style={s.scoreRow}>
                  <div style={s.scoreLabel}>
                    <span>{bar.label}</span>
                    <span style={s.scoreVal}>{val}/{bar.max}</span>
                  </div>
                  <div style={s.scoreTrack}>
                    <div style={{ ...s.scoreFill, width: `${pct}%`, background: bar.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Message from Nitin */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>From Nitin</h3>
          <div style={s.messageBox}>
            <p style={s.messageText}>{outcomeData.message}</p>
          </div>
        </div>

        {/* Specialist note */}
        {result.specialistNoteTriggered && outcomeData.specialistNote && (
          <div style={s.specialistBox}>
            <p style={s.specialistLabel}>Specialist Note</p>
            <p style={s.specialistText}>{outcomeData.specialistNote}</p>
          </div>
        )}

        {/* Next steps */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Your Next Steps</h3>
          <div style={s.steps}>
            {outcomeData.steps.map((step, i) => (
              <div key={i} style={s.step}>
                <div style={s.stepNum}>{i + 1}</div>
                <p style={s.stepText}>{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Ask Nitin CTA */}
        <div style={s.askNitinCta} onClick={() => navigate('/ask')}>
          <div style={s.askNitinIcon}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div>
            <p style={s.askNitinTitle}>Still confused? Ask Nitin</p>
            <p style={s.askNitinDesc}>Get personalised, experience-backed guidance on your career questions.</p>
          </div>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--gold)' }}>
            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
          </svg>
        </div>

        <p style={s.completedAt}>
          Completed on {new Date(result.completedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>
    </div>
  );
}

const s = {
  page: { background: 'var(--bg)', minHeight: 'calc(100vh - 60px)', display: 'flex', justifyContent: 'center', padding: '24px 16px' },
  container: { maxWidth: '680px', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    background: 'var(--navy)', color: '#fff', borderRadius: '16px',
    padding: '28px 28px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '10px',
  },
  headerNav: { width: '100%', display: 'flex', justifyContent: 'flex-start' },
  backBtn: { background: 'rgba(255,255,255,0.1)', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer', padding: '6px 12px', borderRadius: '8px' },
  outcomeEmoji: { fontSize: '44px', lineHeight: 1 },
  outcomeTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '24px', fontWeight: '700', color: '#fff', margin: 0 },
  practiceArea: { fontSize: '13px', color: 'var(--gold-mid)', background: 'rgba(232,208,138,0.12)', padding: '6px 16px', borderRadius: '99px', border: '1px solid rgba(232,208,138,0.25)' },
  outcomeDesc: { fontSize: '14px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.7', maxWidth: '480px', margin: 0 },
  section: { background: '#fff', borderRadius: '14px', padding: '22px', boxShadow: 'var(--shadow)' },
  sectionTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '16px', fontWeight: '700', color: 'var(--navy)', marginBottom: '16px' },
  scoreBars: { display: 'flex', flexDirection: 'column', gap: '14px' },
  scoreRow: { display: 'flex', flexDirection: 'column', gap: '5px' },
  scoreLabel: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', color: 'var(--text-2)' },
  scoreVal: { color: 'var(--navy)', fontWeight: '700' },
  scoreTrack: { height: '8px', background: 'var(--border)', borderRadius: '99px', overflow: 'hidden' },
  scoreFill: { height: '100%', borderRadius: '99px', transition: 'width 0.6s ease' },
  messageBox: { background: 'var(--bg)', borderRadius: '10px', padding: '18px', border: '1px solid var(--border)' },
  messageText: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.75', margin: 0 },
  specialistBox: { background: 'var(--gold-light)', borderRadius: '14px', padding: '20px', border: '1px solid var(--gold-mid)' },
  specialistLabel: { fontSize: '11px', fontWeight: '700', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  specialistText: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.7', margin: 0 },
  steps: { display: 'flex', flexDirection: 'column', gap: '12px' },
  step: { display: 'flex', gap: '14px', alignItems: 'flex-start' },
  stepNum: {
    width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
    background: 'var(--navy)', color: '#fff', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700',
  },
  stepText: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.65', paddingTop: '4px', margin: 0 },
  askNitinCta: {
    background: 'var(--gold-light)', border: '1.5px solid var(--gold-mid)',
    borderRadius: '14px', padding: '20px 22px',
    display: 'flex', alignItems: 'center', gap: '16px',
    cursor: 'pointer', transition: 'box-shadow 0.15s',
  },
  askNitinIcon: {
    width: '46px', height: '46px', borderRadius: '12px',
    background: 'var(--navy)', color: 'var(--gold-mid)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  askNitinTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '16px', fontWeight: '700', color: 'var(--navy)', margin: 0,
  },
  askNitinDesc: { fontSize: '13px', color: 'var(--text-2)', margin: '3px 0 0', lineHeight: '1.5' },
  actions: { display: 'flex', gap: '12px' },
  btnSecondary: {
    flex: 1, background: 'var(--navy)', color: '#fff', border: 'none',
    padding: '13px', borderRadius: '10px', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  completedAt: { fontSize: '11px', color: 'var(--text-3)', textAlign: 'center' },
  spinner: { width: '28px', height: '28px', borderRadius: '50%', border: '2px solid var(--border)', borderTop: '2px solid var(--navy)', animation: 'spin 0.8s linear infinite', margin: 'auto', marginTop: '20vh' },
  errorTxt: { color: 'var(--danger)', textAlign: 'center', marginTop: '40px', fontSize: '14px' },
};
